import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import VideoFacade from '../components/VideoFacade';
import ContactForm from '../components/ContactForm';
import { COMPANY_INFO, CORE_SERVICES, MAIN_AREAS } from '../data/siteData';
import { imageUrl } from '../lib/images';

export default function HomePage() {
  const [servicesInView, setServicesInView] = useState(false);
  const servicesSectionRef = useRef(null);

  useEffect(() => {
    const el = servicesSectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setServicesInView(true);
        }
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <>
      <SEO
        title="Hærvejens Træpleje - Certificerede og uddannede Arborister"
        description="Hærvejens Træpleje tilbyder professionel træpleje for private og erhverv. Fra træfældning og topkapning til beskæring og rodfræsning."
        ogImage="https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/traefaeldning.jpg"
      />

      {/* Hero Section matching Divi Landscape structure */}
      <section className="relative bg-[#0f241a] text-white overflow-hidden py-24 sm:py-28 lg:py-36 min-h-[680px] lg:min-h-[760px] flex items-center">
        {/* Background Images with Dark Transparent Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/_assets/imgi-35-hero-bg.jpg"
            alt="Træpleje og landskab hero baggrund"
            className="w-full h-full object-cover object-center"
          />
          <img
            src="/_assets/imgi-34-bgform-scaled-1.jpg"
            alt="Træpleje baggrundstekstur"
            className="absolute inset-0 w-full h-full object-cover object-center mix-blend-overlay opacity-30 pointer-events-none"
          />
          {/* Dark transparent overlay */}
          <div className="absolute inset-0 bg-black/55 backdrop-brightness-75 pointer-events-none" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Left Content Block */}
            <div className="lg:col-span-7 space-y-6">
              {/* Eyebrow / Overline */}
              <p className="text-xs sm:text-sm font-semibold tracking-wider text-[#a5d8bd]">
                Certificerede og uddannede arborister fra Skovskolen
              </p>

              {/* Main Headline H1 */}
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.12]">
                Træpleje i Jylland og på Fyn
              </h1>

              {/* Body Paragraph */}
              <p className="text-sm sm:text-base lg:text-lg text-white/90 leading-relaxed max-w-2xl">
                Vi laver alt indenfor <strong className="text-white">træfældning</strong>, <strong className="text-white">topkapning</strong>, <strong className="text-white">rodfræsning</strong> og <strong className="text-white">beskæring af træer</strong>. Vi sikrer dig altid en høj faglighed og grundig rådgivning.
              </p>

              {/* Primary Action Button (Divi landscape style) */}
              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Link
                  to="/kontakt"
                  className="px-8 py-3.5 bg-[#2d6a4f] hover:bg-[#23533e] text-white text-xs sm:text-sm font-bold rounded-sm transition-all shadow-md inline-flex items-center gap-2 cursor-pointer"
                >
                  <i className="fa-solid fa-file-signature text-xs" aria-hidden="true" />
                  <span>Indhent tilbud</span>
                </Link>

                <a
                  href={`tel:${COMPANY_INFO.phoneRaw}`}
                  className="px-6 py-3.5 bg-white/10 hover:bg-white/20 text-white border border-white/30 text-xs sm:text-sm font-bold rounded-sm transition-all inline-flex items-center gap-2"
                >
                  <i className="fa-solid fa-phone text-xs text-[#52b788]" aria-hidden="true" />
                  <span>Ring {COMPANY_INFO.phone}</span>
                </a>

                <Link
                  to="/services"
                  className="text-xs sm:text-sm font-semibold text-white/80 hover:text-white underline underline-offset-4 tracking-wide"
                >
                  Se alle ydelser »
                </Link>
              </div>

              {/* Quick points */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs text-[#b8d4c5] pt-6 border-t border-white/20">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-shield-halved text-[#52b788]" aria-hidden="true" />
                  <span>10 mio. forsikring</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-broom text-[#52b788]" aria-hidden="true" />
                  <span>Pæn oprydning altid</span>
                </div>
                <div className="flex items-center gap-2 col-span-2 sm:col-span-1">
                  <i className="fa-solid fa-tags text-[#52b788]" aria-hidden="true" />
                  <span>Fast & ærligt tilbud</span>
                </div>
              </div>
            </div>

            {/* Right Content Block: 2x2 Transparent Card Grid */}
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-4">
                {/* Hero Card 1: Træfældning */}
                <Link
                  to="/traefaeldning"
                  className="bg-black/30 backdrop-blur-md border border-white/20 p-5 sm:p-6 aspect-square flex flex-col justify-between rounded-sm hover:bg-black/45 hover:border-white/40 transition-all group"
                >
                  <div>
                    <div className="w-8 h-8 rounded-sm bg-white/15 text-[#52b788] flex items-center justify-center text-xs mb-3">
                      <i className="fa-solid fa-tree" aria-hidden="true" />
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-white group-hover:text-[#a5d8bd] transition-colors leading-snug">
                      Træfældning
                    </h3>
                    <p className="text-[11px] sm:text-xs text-white/80 mt-1 line-clamp-2">
                      Fældning af store og vanskelige træer med præcision.
                    </p>
                  </div>
                  <span className="text-[11px] sm:text-xs font-bold text-[#52b788] group-hover:text-[#a5d8bd] inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                    <span>Læs mere</span>
                    <i className="fa-solid fa-circle-chevron-right text-[10px]" aria-hidden="true" />
                  </span>
                </Link>

                {/* Hero Card 2: Topkapning */}
                <Link
                  to="/topkapning"
                  className="bg-black/30 backdrop-blur-md border border-white/20 p-5 sm:p-6 aspect-square flex flex-col justify-between rounded-sm hover:bg-black/45 hover:border-white/40 transition-all group"
                >
                  <div>
                    <div className="w-8 h-8 rounded-sm bg-white/15 text-[#52b788] flex items-center justify-center text-xs mb-3">
                      <i className="fa-solid fa-person-digging" aria-hidden="true" />
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-white group-hover:text-[#a5d8bd] transition-colors leading-snug">
                      Topkapning
                    </h3>
                    <p className="text-[11px] sm:text-xs text-white/80 mt-1 line-clamp-2">
                      Sektionsvis nedfiring i trange haver ved bygninger.
                    </p>
                  </div>
                  <span className="text-[11px] sm:text-xs font-bold text-[#52b788] group-hover:text-[#a5d8bd] inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                    <span>Læs mere</span>
                    <i className="fa-solid fa-circle-chevron-right text-[10px]" aria-hidden="true" />
                  </span>
                </Link>

                {/* Hero Card 3: Stub- & Rodfræsning */}
                <Link
                  to="/rodfraesning"
                  className="bg-black/30 backdrop-blur-md border border-white/20 p-5 sm:p-6 aspect-square flex flex-col justify-between rounded-sm hover:bg-black/45 hover:border-white/40 transition-all group"
                >
                  <div>
                    <div className="w-8 h-8 rounded-sm bg-white/15 text-[#52b788] flex items-center justify-center text-xs mb-3">
                      <i className="fa-solid fa-gear" aria-hidden="true" />
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-white group-hover:text-[#a5d8bd] transition-colors leading-snug">
                      Rodfræsning
                    </h3>
                    <p className="text-[11px] sm:text-xs text-white/80 mt-1 line-clamp-2">
                      Effektiv fjernelse af rødder og stubbe under græsplænen.
                    </p>
                  </div>
                  <span className="text-[11px] sm:text-xs font-bold text-[#52b788] group-hover:text-[#a5d8bd] inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                    <span>Læs mere</span>
                    <i className="fa-solid fa-circle-chevron-right text-[10px]" aria-hidden="true" />
                  </span>
                </Link>

                {/* Hero Card 4: Beskæring af træer */}
                <Link
                  to="/beskaering-af-traeer"
                  className="bg-black/30 backdrop-blur-md border border-white/20 p-5 sm:p-6 aspect-square flex flex-col justify-between rounded-sm hover:bg-black/45 hover:border-white/40 transition-all group"
                >
                  <div>
                    <div className="w-8 h-8 rounded-sm bg-white/15 text-[#52b788] flex items-center justify-center text-xs mb-3">
                      <i className="fa-solid fa-scissors" aria-hidden="true" />
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-white group-hover:text-[#a5d8bd] transition-colors leading-snug">
                      Beskæring
                    </h3>
                    <p className="text-[11px] sm:text-xs text-white/80 mt-1 line-clamp-2">
                      Faglig kronepleje, udtynding og sundhedsvurdering.
                    </p>
                  </div>
                  <span className="text-[11px] sm:text-xs font-bold text-[#52b788] group-hover:text-[#a5d8bd] inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                    <span>Læs mere</span>
                    <i className="fa-solid fa-circle-chevron-right text-[10px]" aria-hidden="true" />
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Vores kerneydelser (Design Reference: 4-column cards with tall rounded image, top-left accent badge, lower title+arrow bar, pill CTA & desktop scroll animation) */}
      <section
        ref={servicesSectionRef}
        className="py-20 sm:py-24 lg:py-28 bg-[#f5f8f5] transition-colors"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Centered Section Header Area with Pill Eyebrow & Two-line Display Heading */}
          <div
            className={`flex flex-col items-center text-center pb-12 sm:pb-16 max-w-3xl mx-auto motion-reduce:transform-none motion-reduce:opacity-100 lg:transition-all lg:duration-700 lg:ease-out ${
              servicesInView
                ? 'lg:opacity-100 lg:translate-y-0'
                : 'lg:opacity-0 lg:translate-y-8'
            }`}
          >
            {/* Pill-shaped eyebrow label with delicate border */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wider text-[#163627] bg-white border border-[#163627]/15 shadow-2xs mb-4">
              <i className="fa-solid fa-tree text-[#2d6a4f] text-[11px]" aria-hidden="true" />
              <span>Vores kerneydelser</span>
            </div>

            {/* Large, prominent, two-line display heading */}
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#14291d] leading-[1.16]">
              Professionel træpleje med<br className="hidden sm:inline" /> kvalitet og sikkerhed i fokus
            </h2>

            {/* Subtle intro description */}
            <p className="text-sm sm:text-base text-zinc-600 mt-4 max-w-xl leading-relaxed">
              Vi løser enhver opgave med motorsav, klatregrej og specialmaskiner med fokus på træets biologi, din sikkerhed og et pænt resultat.
            </p>
          </div>

          {/* 4-Column Grid of Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 xl:gap-7">
            {CORE_SERVICES.slice(0, 4).map((service, index) => {
              // Stagger delay for desktop animation
              const delayClass = [
                'lg:delay-100',
                'lg:delay-200',
                'lg:delay-300',
                'lg:delay-400',
              ][index] || 'lg:delay-100';

              return (
                <Link
                  key={service.slug}
                  to={`/${service.slug}`}
                  className={`group flex flex-col rounded-[1.75rem] bg-[#e8efe9] p-3 border border-[#163627]/8 shadow-[0_6px_24px_-8px_rgba(22,54,39,0.07)] hover:shadow-[0_18px_36px_-10px_rgba(22,54,39,0.16)] hover:-translate-y-1.5 transition-all duration-300 motion-reduce:transform-none motion-reduce:opacity-100 lg:duration-700 lg:ease-out ${delayClass} ${
                    servicesInView
                      ? 'lg:opacity-100 lg:translate-y-0'
                      : 'lg:opacity-0 lg:translate-y-12'
                  }`}
                >
                  {/* Tall vertical image with significantly rounded corners */}
                  <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-zinc-200 shadow-inner">
                    <img
                      src={imageUrl(service.image, 'width=600,quality=80,format=auto')}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-106 transition-transform duration-500 ease-out"
                      loading="lazy"
                    />

                    {/* Circular icon badge in top-left with accent color background and primary text color */}
                    <div
                      className="absolute top-3.5 left-3.5 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#52b788] text-[#14291d] flex items-center justify-center shadow-md shadow-[#163627]/20 z-10 text-sm sm:text-base font-bold transition-transform duration-300 group-hover:scale-110"
                      aria-hidden="true"
                    >
                      <i className={`fa-solid ${service.icon}`} />
                    </div>
                  </div>

                  {/* Lower section containing title on the left and circular arrow button on the right */}
                  <div className="pt-3.5 pb-1 px-2.5 flex items-center justify-between gap-3 mt-auto">
                    <h3 className="text-base sm:text-lg font-semibold text-[#14291d] group-hover:text-[#2d6a4f] transition-colors leading-snug line-clamp-2">
                      {service.title}
                    </h3>
                    <span
                      className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white text-[#14291d] group-hover:bg-[#52b788] group-hover:text-[#14291d] flex items-center justify-center shadow-2xs transition-all duration-300"
                      aria-label={`Læs mere om ${service.title}`}
                    >
                      <i className="fa-solid fa-arrow-right text-xs transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Centered pill-shaped primary CTA button utilizing accent color */}
          <div
            className={`mt-14 sm:mt-16 text-center motion-reduce:transform-none motion-reduce:opacity-100 lg:transition-all lg:duration-700 lg:ease-out lg:delay-500 ${
              servicesInView
                ? 'lg:opacity-100 lg:translate-y-0'
                : 'lg:opacity-0 lg:translate-y-6'
            }`}
          >
            <Link
              to="/services"
              className="inline-flex items-center gap-3 px-8 py-3.5 sm:py-4 bg-[#52b788] hover:bg-[#40916c] text-[#0f241a] hover:text-white font-bold text-sm sm:text-base rounded-full transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 group"
            >
              <span>Se alle ydelser & specialer</span>
              <span className="w-6 h-6 rounded-full bg-[#0f241a]/10 group-hover:bg-white/20 flex items-center justify-center transition-colors">
                <i className="fa-solid fa-arrow-right text-xs transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Section 5: Overlapping Feature Banner (Reference Image Design Specification) */}
      <section className="relative w-full bg-white">
        {/* Top spacing area */}
        <div className="h-16 sm:h-24 bg-[#f5f8f5]" />

        {/* Hero / Banner Area */}
        <div className="relative w-full min-h-[480px] sm:min-h-[520px] lg:min-h-[580px] bg-neutral-900">
          {/* Background Image */}
          <img
            src="/_assets/imgi-34-bgform-scaled-1.jpg"
            onError={(e) => {
              if (!e.currentTarget.dataset.fallback) {
                e.currentTarget.dataset.fallback = 'true';
                e.currentTarget.src = 'https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/imgi-34-bgform-scaled-1.jpg';
              }
            }}
            alt="Grøn ekspertise fra rod til krone"
            className="absolute inset-0 w-full h-full object-cover object-center"
            loading="lazy"
          />

          {/* Overlapping Container */}
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex justify-end">
            {/* Feature Card with Zero Radius, Zero Drop Shadow, Negative Overlap */}
            <div className="relative -top-16 sm:-top-24 w-full max-w-[480px] bg-[#163627] p-8 sm:p-12 text-white rounded-none shadow-xl border-t-4 border-[#52b788]">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 border border-white/20 rounded-full text-[11px] font-semibold text-[#52b788] mb-4">
                <i className="fa-solid fa-tree text-xs" aria-hidden="true" />
                <span>Kvalitet & erfaring</span>
              </div>

              {/* Heading */}
              <h2 className="text-2xl sm:text-3xl font-extrabold leading-snug tracking-tight text-white">
                Grøn ekspertise fra rod til krone!
              </h2>

              {/* Decorative Accent Line */}
              <div className="w-12 h-1 bg-[#52b788] mt-3.5 mb-4 rounded-full" />

              {/* Description */}
              <p className="text-sm sm:text-base font-normal leading-relaxed text-zinc-100">
                Vi plejer naturen og sikrer sunde, velholdte træer med respekt for omgivelserne.
              </p>

              {/* Action / Arrow Links - Styled & High Visibility */}
              <div className="mt-8 flex flex-col space-y-3.5">
                <Link
                  to="/traefaeldning"
                  className="group flex items-center justify-between p-3.5 sm:p-4 bg-white/10 hover:bg-white/20 border border-white/15 hover:border-[#52b788] transition-all duration-200 text-white font-medium text-sm sm:text-base rounded-sm shadow-xs"
                >
                  <span className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-[#52b788]/20 text-[#52b788] flex items-center justify-center group-hover:bg-[#52b788] group-hover:text-[#163627] transition-all">
                      <i className="fa-solid fa-chevron-right text-xs" aria-hidden="true" />
                    </span>
                    <span className="font-semibold text-white">Professionel træfældning & topkapning</span>
                  </span>
                  <i className="fa-solid fa-arrow-right text-xs text-[#52b788] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </Link>

                <Link
                  to="/rodfraesning"
                  className="group flex items-center justify-between p-3.5 sm:p-4 bg-white/10 hover:bg-white/20 border border-white/15 hover:border-[#52b788] transition-all duration-200 text-white font-medium text-sm sm:text-base rounded-sm shadow-xs"
                >
                  <span className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-[#52b788]/20 text-[#52b788] flex items-center justify-center group-hover:bg-[#52b788] group-hover:text-[#163627] transition-all">
                      <i className="fa-solid fa-chevron-right text-xs" aria-hidden="true" />
                    </span>
                    <span className="font-semibold text-white">Effektiv rodfræsning & stubfræsning</span>
                  </span>
                  <i className="fa-solid fa-arrow-right text-xs text-[#52b788] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </Link>

                <Link
                  to="/kontakt"
                  className="group flex items-center justify-between p-3.5 sm:p-4 bg-[#52b788] hover:bg-[#40916c] text-[#0f241a] hover:text-white font-bold text-sm sm:text-base transition-all duration-200 rounded-sm shadow-sm"
                >
                  <span className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-[#0f241a]/15 flex items-center justify-center text-[#0f241a] group-hover:text-white group-hover:bg-white/20 transition-all">
                      <i className="fa-solid fa-calculator text-xs" aria-hidden="true" />
                    </span>
                    <span>Indhent uforpligtende tilbud</span>
                  </span>
                  <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Local Coverage Section: Vi tilbyder træpleje lokalt i: */}
      <section className="py-16 sm:py-24 bg-white border-y border-[#e5ece7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-xs font-bold text-[#296046] bg-[#e4eee8] px-3 py-1 rounded-full">
                Lokal forankring
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#14291d] tracking-tight mt-3">
                Vi tilbyder træpleje lokalt i:
              </h2>
              <p className="text-[#495b50] text-base mt-2 max-w-2xl">
                Vi er dybt forankret i den jyske muld og er stolte af at servicere en bred vifte af byer. Vores lokale kendskab sikrer, at vi forstår de specifikke forhold i netop dit område.
              </p>
            </div>
            <Link
              to="/omraader"
              className="inline-flex items-center gap-2 text-sm font-bold text-[#1b4332] hover:underline whitespace-nowrap self-start lg:self-auto"
            >
              <span>Gå til samlet områdehub</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6 items-center">
            {MAIN_AREAS.map((area) => (
              <Link
                key={area.slug}
                to={`/${area.slug}`}
                aria-label={`Træpleje i ${area.name}`}
                className="group relative block p-2 sm:p-2.5 bg-white rounded-xl border border-[#d6e2db] shadow-xs hover:border-[#2d6a4f] hover:shadow-md transition-all"
              >
                <img
                  src={area.image}
                  alt={`Træpleje i ${area.name}`}
                  className="w-full h-auto object-contain block group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4: Narrative wrapped around video for an organic, premium editorial layout */}
      <section className="py-16 sm:py-24 bg-[#fbfbfa] border-t border-zinc-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div>
            <p className="text-xs font-bold text-[#2d6a4f] mb-2">
              Mange års erfaring & faglig stolthed
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#14291d] tracking-tight leading-tight max-w-4xl">
              Din garanti for et sikkert og professionelt stykke arbejde
            </h2>
            <h3 className="text-base sm:text-lg font-semibold text-[#2d6a4f] mt-2 mb-8">
              Vi sikrer dig en høj faglighed og grundig rådgivning
            </h3>
          </div>

          {/* Flow container: Video floats right on md+, text wraps gracefully around it */}
          <div className="flow-root">
            <div className="w-full md:w-[48%] lg:w-[46%] md:float-right md:ml-8 lg:ml-12 mb-6 sm:mb-8">
              <VideoFacade
                title="Hærvejens Træpleje & Hærvejsgruppen Videopræsentation"
                videoId="oawrw1sGUrw"
                provider="youtube"
              />
            </div>

            <div className="space-y-4 text-zinc-700 leading-relaxed text-sm sm:text-base">
              <p>
                Velkommen til Hærvejens Træpleje. Mit navn er Mathias Svendsen, og jeg er stolt af at drive en virksomhed, hvor godt håndværk, grundig rådgivning og din sikkerhed er det absolutte omdrejningspunkt. Vi er et hold af 12 erfarne og certificerede arborister, uddannet fra Skovskolen, som er dedikerede til at levere professionel <strong>træpleje</strong> i hele Østjylland og Trekantområdet.
              </p>
              <p>
                For os handler træpleje om mere end bare at bruge en motorsav. Det handler om at forstå træets biologi, vurdere enhver opgave med et trænet øje og udføre arbejdet med en præcision, der beskytter dine værdier og efterlader din have i perfekt stand.
              </p>
              <p>
                At vælge den rette til at håndtere dine træer er en tillidssag. Hos Hærvejens Træpleje er din tryghed vores ansvar. Derfor er vi fuldt forsikrede gennem Topdanmark for op til 10 millioner kroner, og vi overholder altid Arbejdstilsynets strenge regler for sikkerhed og erhvervsmæssig klatring.
              </p>

              {/* Founder quote box */}
              <div className="bg-white p-5 sm:p-6 rounded-sm border border-zinc-200 shadow-xs my-6">
                <p className="text-sm sm:text-base italic text-zinc-700">
                  "Vi forlader ikke din have, før den er pæn og ryddelig. For os er en veludført opgave lig med en tilfreds kunde."
                </p>
                <p className="text-xs font-bold text-[#14291d] mt-2">
                  Mathias Svendsen — Indehaver, Hærvejens Træpleje
                </p>
              </div>

              {/* Action Buttons */}
              <div className="pt-2 flex flex-wrap gap-3">
                <Link
                  to="/om-os"
                  className="px-6 py-3 bg-[#163627] hover:bg-[#0f241a] text-white text-xs font-bold rounded-sm transition-colors shadow-xs"
                >
                  Læs hele historien om teamet
                </Link>
                <a
                  href={`tel:${COMPANY_INFO.phoneRaw}`}
                  className="px-6 py-3 bg-white border border-[#2d6a4f] text-[#2d6a4f] hover:bg-[#2d6a4f] hover:text-white text-xs font-bold rounded-sm transition-colors whitespace-nowrap shadow-xs"
                >
                  Ring nu: {COMPANY_INFO.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values & Guarantees Section (Restyled to match design reference #454cde17-32fa-4042-8666-2e0b2170c852) */}
      <section className="py-20 sm:py-28 bg-[#fbfbfa] border-t border-zinc-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Left Column: Introduction & Massive Statistical Lockup */}
            <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-28">
              <div>
                <span className="text-xs font-bold text-[#2d6a4f] mb-3 block">
                  Vores faglige garanti
                </span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-[#14291d] tracking-tight leading-tight">
                  Hvorfor vælge os? Din garanti for sikkerhed og kvalitet
                </h2>
                <p className="text-base text-[#495b50] leading-relaxed mt-4">
                  I en branche, hvor arborist ikke er en beskyttet titel, er det afgørende at vælge et team, du kan stole på.
                </p>
              </div>

              {/* Massive Statistical Lockup (using website font, bold, non-italic) */}
              <div className="pt-4 border-t border-zinc-200">
                <div className="relative inline-block">
                  <div className="text-6xl sm:text-7xl lg:text-8xl font-black text-[#52b788] tracking-tight leading-none">
                    10M
                  </div>
                  <div className="mt-2 text-sm sm:text-base font-semibold text-[#14291d]">
                    kr. ansvarsforsikring hos Topdanmark & 100% uddannet personale
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-3 px-6 py-3.5 bg-[#163627] hover:bg-[#204936] text-white font-bold text-sm rounded-sm transition-colors shadow-xs"
                >
                  <span>Indhent uforpligtende tilbud</span>
                  <i className="fa-solid fa-arrow-right text-xs text-[#52b788]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Right Column: Vertical List with Oversized Numerals and Thin Horizontal Dividers */}
            <div className="lg:col-span-7">
              <div className="divide-y divide-zinc-200 border-y border-zinc-200">
                {/* Item 01 */}
                <div className="py-7 sm:py-9 flex flex-col sm:flex-row sm:items-baseline gap-4 sm:gap-8">
                  <div className="text-4xl sm:text-5xl font-black text-[#52b788] sm:w-20 shrink-0 leading-none">
                    01
                  </div>
                  <div className="flex-1 space-y-2">
                    <h3 className="text-xl sm:text-2xl font-bold text-[#14291d] tracking-tight">
                      Uddannede fagfolk
                    </h3>
                    <p className="text-sm sm:text-base text-[#3b4d42] leading-relaxed">
                      Alle vores medarbejdere er uddannede arborister fra Skovskolen eller er lærlinge på vej mod samme mål. Vi ansætter kun folk, vi kender og stoler på fagligt.
                    </p>
                  </div>
                </div>

                {/* Item 02 */}
                <div className="py-7 sm:py-9 flex flex-col sm:flex-row sm:items-baseline gap-4 sm:gap-8">
                  <div className="text-4xl sm:text-5xl font-black text-[#52b788] sm:w-20 shrink-0 leading-none">
                    02
                  </div>
                  <div className="flex-1 space-y-2">
                    <h3 className="text-xl sm:text-2xl font-bold text-[#14291d] tracking-tight">
                      Kompromisløs sikkerhed
                    </h3>
                    <p className="text-sm sm:text-base text-[#3b4d42] leading-relaxed">
                      Vi overholder altid Arbejdstilsynets regler. Det betyder, at vi minimum er to mand på klatreopgaver, hvor personen på jorden er uddannet til at udføre en redningsaktion.
                    </p>
                  </div>
                </div>

                {/* Item 03 */}
                <div className="py-7 sm:py-9 flex flex-col sm:flex-row sm:items-baseline gap-4 sm:gap-8">
                  <div className="text-4xl sm:text-5xl font-black text-[#52b788] sm:w-20 shrink-0 leading-none">
                    03
                  </div>
                  <div className="flex-1 space-y-2">
                    <h3 className="text-xl sm:text-2xl font-bold text-[#14291d] tracking-tight">
                      Fuld forsikring
                    </h3>
                    <p className="text-sm sm:text-base text-[#3b4d42] leading-relaxed">
                      Vi er fuldt forsikret for op til 10 millioner kroner pr. skade hos Topdanmark. Du kan være helt tryg, når vi arbejder på din grund.
                    </p>
                  </div>
                </div>

                {/* Item 04 */}
                <div className="py-7 sm:py-9 flex flex-col sm:flex-row sm:items-baseline gap-4 sm:gap-8">
                  <div className="text-4xl sm:text-5xl font-black text-[#52b788] sm:w-20 shrink-0 leading-none">
                    04
                  </div>
                  <div className="flex-1 space-y-2">
                    <h3 className="text-xl sm:text-2xl font-bold text-[#14291d] tracking-tight">
                      Ungt og effektivt team
                    </h3>
                    <p className="text-sm sm:text-base text-[#3b4d42] leading-relaxed">
                      Vores team består af unge, energiske fagfolk i 20’erne, som er passionerede omkring deres arbejde. Vi er effektive, grundige og altid klar til at yde vores bedste.
                    </p>
                  </div>
                </div>

                {/* Item 05 */}
                <div className="py-7 sm:py-9 flex flex-col sm:flex-row sm:items-baseline gap-4 sm:gap-8">
                  <div className="text-4xl sm:text-5xl font-black text-[#52b788] sm:w-20 shrink-0 leading-none">
                    05
                  </div>
                  <div className="flex-1 space-y-2">
                    <h3 className="text-xl sm:text-2xl font-bold text-[#14291d] tracking-tight">
                      Ærlighed og gennemsigtighed
                    </h3>
                    <p className="text-sm sm:text-base text-[#3b4d42] leading-relaxed">
                      Vi springer aldrig over, hvor gærdet er lavest. Vi giver dig ærlig rådgivning og et præcist tilbud, så du undgår uforudsete udgifter.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section - Restyled to match "Webform on right" reference */}
      <section className="relative py-20 sm:py-28 lg:py-32 overflow-hidden text-white border-t border-[#1a3d2c]">
        {/* Full-bleed background image with dark overlay to ensure text legibility */}
        <div className="absolute inset-0 z-0">
          <img
            src="/_assets/imgi-34-bgform-scaled-1.jpg"
            alt="Træpleje uforpligtende tilbud baggrund"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-[#07190f]/90 sm:bg-[#07190f]/85 backdrop-brightness-75 pointer-events-none" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left column: Display heading, body text, attribution/links, ghost button */}
            <div className="space-y-6">
              <div>
                <span className="text-xs font-bold text-[#7dd2a6] bg-[#1a3d2c]/80 border border-[#52b788]/30 px-3.5 py-1.5 rounded-full inline-block mb-3">
                  Få et gratis tilbud
                </span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight mt-2 mb-4">
                  Få et uforpligtende tilbud på træpleje
                </h2>
                <p className="text-base sm:text-lg text-white/80 leading-relaxed">
                  Er du klar til at få professionel hjælp til dine træer? Kontakt os i dag for en gratis og uforpligtende snak om din opgave. Vi kommer gerne ud og besigtiger forholdene, så vi kan give dig et fast og konkurrencedygtigt tilbud, du kan regne med – helt uden uforudsete udgifter.
                </p>
              </div>

              {/* Smaller attribution and contact links */}
              <div className="space-y-3.5 py-5 border-y border-white/15 text-sm text-white/80">
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-[#52b788]">
                    Brug for en kyndig specialist?
                  </p>
                  <p className="text-base font-semibold text-white">
                    Ring direkte på{' '}
                    <a
                      href={`tel:${COMPANY_INFO.phoneRaw}`}
                      className="underline decoration-[#52b788] underline-offset-4 text-[#a5e4c2] hover:text-white transition-colors"
                    >
                      {COMPANY_INFO.phone}
                    </a>
                  </p>
                  <p className="text-xs text-white/60">
                    Vi kan træffes alle ugens hverdage på telefon eller e-mail.
                  </p>
                </div>

                <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-xs sm:text-sm text-white/70">
                  <p className="flex items-center gap-2.5">
                    <i className="fa-solid fa-location-dot text-[#52b788] shrink-0" aria-hidden="true" />
                    <span>
                      {COMPANY_INFO.address}, {COMPANY_INFO.postalCode} {COMPANY_INFO.city}
                    </span>
                  </p>
                  <p className="flex items-center gap-2.5">
                    <i className="fa-solid fa-envelope text-[#52b788] shrink-0" aria-hidden="true" />
                    <a href={`mailto:${COMPANY_INFO.email}`} className="hover:text-white underline decoration-white/30 transition-colors">
                      {COMPANY_INFO.email}
                    </a>
                  </p>
                </div>
              </div>

              {/* Outlined primary action button (ghost button) */}
              <div className="pt-2">
                <a
                  href={`tel:${COMPANY_INFO.phoneRaw}`}
                  className="inline-flex items-center justify-center gap-3 px-8 py-3.5 rounded-full border-2 border-white/80 hover:border-white text-white font-semibold text-base hover:bg-white/10 transition-all duration-200"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span>Ring {COMPANY_INFO.phone}</span>
                </a>
              </div>
            </div>

            {/* Right column: Vertical contact form with bottom-border minimal inputs and pill CTA */}
            <div className="w-full">
              <ContactForm variant="minimal-dark" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
